import React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../../../utils/cx';

const SEMANTIC_NAME = 'RomaFormText';

type FormTextElement = 'p' | 'div';

export type FormTextProps = {
    children: ReactNode;
    as?: FormTextElement;
    size?: 'sm' | 'md' | 'lg';
    color?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'error' | 'success';
    align?: 'left' | 'center' | 'right';
    className?: string;
};

const styles = {
    sizes: {
        sm: 'text-sm',
        md: 'text-md',
        lg: 'text-lg',
    },
    color: {
        primary: 'text-primary',
        secondary: 'text-secondary',
        tertiary: 'text-tertiary',
        quaternary: 'text-quaternary',
        error: 'text-error-primary font-medium',
        success: 'text-success-primary font-medium',
    },
    linkHoverColor: {
        primary: '[&_a:hover]:text-secondary_hover',
        secondary: '[&_a:hover]:text-secondary_hover',
        tertiary: '[&_a:hover]:text-tertiary_hover',
        quaternary: '[&_a:hover]:text-quaternary_hover',
        error: '[&_a:hover]:text-error-primary_hover',
        success: '[&_a:hover]:text-success-primary',
    },
    align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    },
    richContent:
        '[&_p:not(:last-child)]:mb-md [&_ul:not(:last-child)]:mb-md [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-2xl [&_ol]:pl-2xl [&_li]:mb-sm',
};

/**
 * Displays standardised text styling for form helpers and messages.
 */
export const FormText: React.FC<FormTextProps> = ({
    as: Component = 'p',
    children,
    size = 'md',
    color = 'secondary',
    align,
    className,
}) => {
    return (
        <Component
            className={cx(
                SEMANTIC_NAME,
                styles.sizes[size],
                styles.color[color],
                align && styles.align[align],
                color === 'error' && 'data-[invalid=true]',
                styles.richContent,
                // link styles
                '[&_a]:underline [&_a]:text-inherit [&_a]:decoration-current [&_a]:underline-offset-2 [&_a]:transition [&_a]:duration-100 [&_a]:ease-linear',
                styles.linkHoverColor[color],
                // link outline for accessibility
                ' [&_a]:focus:outline-none [&_a]:focus-visible:rounded-xxs [&_a]:focus-visible:ring-2 [&_a]:focus-visible:ring-offset-1 [&_a]:focus-visible:ring-primary_focused',
                className,
            )}
        >
            {children}
        </Component>
    );
};

FormText.displayName = SEMANTIC_NAME;
